// =============================================================================
// Partner time helpers — locale-aware relative labels for live surfaces.
// =============================================================================

/**
 * Short relative label for a past instant (e.g. "5 min. ago" / "قبل ٥ دقائق").
 * Falls back to compact latin units when Intl has no data for the locale.
 */
export function formatAgo(
  locale: string,
  deltaMs: number,
  justNow: string
): string {
  if (!Number.isFinite(deltaMs) || deltaMs < 45_000) return justNow;
  try {
    const rtf = new Intl.RelativeTimeFormat(locale || undefined, {
      numeric: "always",
      style: "short",
    });
    const seconds = deltaMs / 1000;
    if (seconds < 90) return rtf.format(-Math.round(seconds), "second");
    const minutes = seconds / 60;
    if (minutes < 60) return rtf.format(-Math.round(minutes), "minute");
    const hours = minutes / 60;
    if (hours < 24) return rtf.format(-Math.floor(hours), "hour");
    return rtf.format(-Math.round(hours / 24), "day");
  } catch {
    const minutes = Math.max(1, Math.round(deltaMs / 60_000));
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    return hours < 24 ? `${hours}h` : `${Math.round(hours / 24)}d`;
  }
}

/** True when the ISO instant is older than `minutes` (false for missing/invalid). */
export function isOlderThan(
  iso: string | null | undefined,
  minutes: number
): boolean {
  if (!iso) return false;
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return false;
  return Date.now() - then >= minutes * 60_000;
}

/**
 * Map the app locale to an Intl locale for dates/numbers.
 * ar → ar-IQ (latin digits per CLDR), ku → ckb, en → en-GB (day-first, regional).
 */
export function appIntlLocale(locale: string): string {
  if (locale === "ar") return "ar-IQ";
  if (locale === "ku") return "ckb";
  return "en-GB";
}